/**
 * SubquerySection — the collapsible wrapper for nested `(SELECT … )` paren
 * groups inside a SELECT statement. Used by FROM (derived tables), JOIN RHS
 * (LATERAL + non-LATERAL derived), WHERE/HAVING (scalar / IN-list), and
 * SELECT-list (scalar) renderers.
 *
 * Two visual variants:
 *   - `mode='block'` — full bordered card, used when the subquery occupies its
 *     own row (FROM item, JOIN RHS).
 *   - `mode='chip'` — compact pill, used when the subquery is inline within
 *     another expression (WHERE pill text, SELECT-list item, IN-list).
 *
 * Both variants ship the same set of affordances:
 *   - chevron toggle (collapsed by default — IS enabled, UI state)
 *   - "focus" drill-in button (IS enabled)
 *   - correlated-reference indicator with tooltip naming the outer alias(es)
 *
 * When expanded the component recursively mounts `<SelectRenderer>` against a
 * synthetic nested statement covering the body byte range; non-SELECT bodies
 * (VALUES) fall through to a structural-summary `<pre>` placeholder.
 *
 * Derived-table header (VQE Polish primitive #9 — COM-21/COM-22): the block
 * header used to lead with the raw SQL (`( SELECT … AS t… )`), truncating
 * away the one thing that actually matters — the alias. For non-LATERAL
 * `mode='block'` subqueries (FROM derived tables + JOIN RHS derived tables)
 * we now scan the text immediately following the closing paren for an alias
 * (`AS alias` or a bare trailing identifier, guarded against clause keywords
 * like WHERE/JOIN/ON so an *unaliased* derived table never misreads the next
 * clause as an alias) and, when found, lead the header with it — the alias
 * NEVER truncates — demoting the SQL to a secondary `truncateMiddle`'d
 * snippet with a full-text tooltip. Chip-mode subqueries (WHERE IN, SELECT-
 * list scalar) and LATERAL derived tables are unaffected — chip adoption is
 * COM-24, deferred to the lane owning `SelectColumnChip`/`FilterPills` this
 * wave; LATERAL keeps its existing `LATERAL ( … )` header.
 */
import { useMemo } from 'react';
import type { ReactNode } from 'react';

import { ChevronDown, ChevronRight, Maximize2 } from 'lucide-react';

import { Badge } from '../../../../../components/ui/badge';
import { Button } from '../../../../../components/ui/button';
import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import { cn } from '../../../../../lib/utils';
import type { ParseOptions } from '../../../../sql-grammar/model';
import type { DrillEntry } from '../../types';
import type { VqeEditContext } from '../../edit/editContext';
import { deriveSubqueryEditContext } from '../../edit/deriveSubqueryEditContext';
import { useVqeStore } from '../../store/vqeStore';
import { truncateMiddle } from '../common/truncateMiddle';
import { VqeTooltip } from '../common/VqeTooltip';
import { SelectRenderer } from './SelectRenderer';
import { findCorrelatedAliases } from './extractSubqueries';
import type { SubqueryBodyKind } from './extractSubqueries';
import { makeNestedSubqueryStatement } from './nestedStatement';
import { resolveDrilledSubqueryBodyEnd } from './resolveDrilledSubqueryBodyEnd';
import { chipSubqueryHint } from './subquerySectionChipHint';

export type SubquerySectionMode = 'block' | 'chip';

export interface SubquerySectionProps {
  readonly bufferText: string;
  readonly bodyStart: number;
  readonly bodyEnd: number;
  readonly bodyKind: SubqueryBodyKind;
  readonly options: ParseOptions;
  readonly parentStatementId: string;
  readonly drillTabId: string;
  readonly drillLabel: string;
  readonly outerAliases: readonly string[];
  readonly mode: SubquerySectionMode;
  readonly testIdPrefix: string;
  readonly isLateral?: boolean;
  /** When set, the chip header reads "<prefixLabel> ( SELECT … )". */
  readonly prefixLabel?: 'IN' | 'LATERAL' | null;
  readonly onEditAsSql: () => void;
  /** When provided, the focus button calls `onDrill(entry)` instead of the global `pushDrill`. */
  readonly onDrill?: (entry: DrillEntry) => void;
  /**
   * When provided, the inline-expanded `<SelectRenderer>` receives a child
   * `VqeEditContext` derived via `deriveSubqueryEditContext` so its inner
   * clauses (e.g. add a WHERE) become editable in place. Threaded by every
   * caller with a live edit-ready `VqeEditContext` in scope: chip-mode
   * WHERE/HAVING/Rail callers, and — as of COM-21/COM-22 — block-mode FROM/
   * JOIN derived-table callers (`FromItemRow`, `JoinLine`), including LATERAL
   * (the resolver's `resolveScope` already restricts a LATERAL body's parent
   * scope to pre-frame aliases, so correlation stays correct with no extra
   * gating here). Omitted (undefined) → the inline body stays read-only.
   */
  readonly editContext?: VqeEditContext;
}

export function SubquerySection(props: SubquerySectionProps) {
  const {
    bufferText,
    bodyStart,
    bodyEnd,
    bodyKind,
    options,
    parentStatementId,
    drillTabId,
    drillLabel,
    outerAliases,
    mode,
    testIdPrefix,
    isLateral = false,
    prefixLabel = null,
    onEditAsSql,
    onDrill,
    editContext,
  } = props;

  const { track } = useAnalytics();
  const expansionKey = `${parentStatementId}:sub:${bodyStart}-${bodyEnd}`;
  const expanded = useVqeStore((s) => s.expansion[expansionKey] ?? false);
  const setExpansion = useVqeStore((s) => s.setExpansion);
  const pushDrill = useVqeStore((s) => s.pushDrill);

  // Unconditional useMemo — hook-order safe. Computes the child editContext for
  // any inline-editable subquery body (bodyKind='select'): chip-mode WHERE/
  // HAVING/Rail callers AND — as of COM-21/COM-22 — block-mode FROM/JOIN
  // derived-table callers (FromItemRow, JoinLine), both of which thread
  // `editContext` when edit-ready. Returns null when the caller omitted
  // `editContext` (not edit-ready / an intentionally read-only surface) or
  // bodyKind isn't 'select'. The whole body is inside a try-catch so that
  // test-stub services (which may omit `getParsedBuffer`) fall back
  // gracefully to read-only on first render.
  const inlineEdit = useMemo(() => {
    if (!editContext || bodyKind !== 'select') return null;
    try {
      const source =
        editContext.service.getParsedBuffer(editContext.documentId)?.parsedDocument.source ?? bufferText;
      const end = resolveDrilledSubqueryBodyEnd(source, bodyStart, options) ?? bodyEnd;
      const ctx = deriveSubqueryEditContext(editContext, editContext.statementId, bodyStart, end);
      return { ctx, source, end };
    } catch {
      return null; // document not open yet / stub service → fall back to read-only
    }
  }, [editContext, bodyKind, bodyStart, bodyEnd, options, bufferText]);

  const correlated = findCorrelatedAliases(bufferText, bodyStart, bodyEnd, outerAliases, options);
  const isCorrelated = correlated.length > 0;

  const headerLabel = isLateral
    ? `LATERAL ( ${truncated(bufferText, bodyStart, bodyEnd)} )`
    : `( ${truncated(bufferText, bodyStart, bodyEnd)} )`;

  // COM-21/COM-22: non-LATERAL block-mode derived tables (FROM + JOIN RHS)
  // lead the header with their alias instead of the raw SQL. Chip mode and
  // LATERAL keep the original `headerLabel` above.
  const derivedAlias = mode === 'block' && !isLateral ? deriveDerivedTableAlias(bufferText, bodyEnd) : null;
  const rawSnippet = collapseBody(bufferText, bodyStart, bodyEnd);

  const containerClass =
    mode === 'block'
      ? 'rounded-md border border-border bg-muted/30 px-2 py-1.5 flex flex-col gap-1.5'
      : expanded
        ? 'flex flex-col gap-1.5 rounded-md border border-border bg-muted/30 px-2 py-1.5 align-middle'
        : 'inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/30 px-2 py-0.5 align-middle';

  return (
    <span
      className={containerClass}
      data-testid={testIdPrefix}
      data-mode={mode}
      data-correlated={isCorrelated ? 'true' : 'false'}
      data-body-kind={bodyKind}
      data-prefix={prefixLabel ?? ''}
      data-lateral={isLateral ? 'true' : 'false'}
    >
      <span className="flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpansion(expansionKey, !expanded)}
          className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:text-foreground"
          data-testid={`${testIdPrefix}-toggle`}
          aria-label={expanded ? 'Collapse subquery' : 'Expand subquery'}
        >
          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
        {prefixLabel ? (
          <Badge variant="outline" className="text-[10px] uppercase" data-testid={`${testIdPrefix}-prefix`}>
            {prefixLabel}
          </Badge>
        ) : null}
        {derivedAlias ? (
          <span className="flex min-w-0 flex-1 items-baseline gap-1.5" data-testid={`${testIdPrefix}-summary`}>
            <span
              className="shrink-0 whitespace-nowrap font-mono text-[11px] font-semibold text-foreground"
              data-testid={`${testIdPrefix}-alias`}
            >
              {derivedAlias}
            </span>
            <span className="shrink-0 text-[10px] text-muted-foreground">— derived table</span>
            {/* `shrink` keeps the tooltip wrapper (the real flex item) from
                pinning its min-width to the whole nowrap snippet, so the inner
                `truncate` actually engages inside a narrow compact chip. */}
            <VqeTooltip content={`(${rawSnippet})`} shrink>
              <span
                className="min-w-0 flex-1 truncate font-mono text-[10px] text-muted-foreground"
                data-testid={`${testIdPrefix}-snippet`}
              >
                ({truncateMiddle(rawSnippet, 40)})
              </span>
            </VqeTooltip>
          </span>
        ) : (
          <VqeTooltip content={chipTooltipContent(mode, isLateral, rawSnippet)}>
            <span
              className="font-mono text-[11px] text-foreground"
              data-testid={`${testIdPrefix}-summary`}
            >
              {headerLabel}
            </span>
          </VqeTooltip>
        )}
        {isCorrelated ? (
          <VqeTooltip content={`References outer alias \`${correlated.join('`, `')}\``}>
            <span
              className={cn('text-[12px] text-warning', mode === 'block' && 'inline-flex')}
              data-testid={`${testIdPrefix}-correlated`}
              aria-label={`Correlated subquery: references outer alias ${correlated.join(', ')}`}
            >
              ↗
            </span>
          </VqeTooltip>
        ) : null}
        <VqeTooltip content="Focus on this subquery">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              const entry: DrillEntry = {
                tabId: drillTabId,
                parentStatementId,
                bodyStart,
                bodyEnd,
                label: drillLabel,
              };
              track('feature:vqe_drill', {
                depth: useVqeStore.getState().drillStack.length + 1,
                source: 'subquery',
              });
              (onDrill ?? pushDrill)(entry);
            }}
            className="h-6 gap-1 px-1.5 text-[11px] text-muted-foreground"
            data-testid={`${testIdPrefix}-focus`}
            aria-label={`Focus on ${drillLabel}`}
          >
            <Maximize2 className="h-3 w-3" />
            focus
          </Button>
        </VqeTooltip>
      </span>

      {expanded ? (
        <span className="flex w-full flex-col" data-testid={`${testIdPrefix}-body`}>
          {bodyKind === 'select' ? (
            // VQE-Edit v1.1 + COM-21/COM-22: chip-mode (WHERE/HAVING/Rail) AND
            // block-mode (FROM/JOIN derived-table) callers thread `editContext`
            // when edit-ready, so `inlineEdit?.ctx` is populated and Add-WHERE /
            // AND/OR + click-to-edit work inline without a drill-in first.
            // `FromItemRow` and `JoinLine` forward their statement-level
            // `editContext` gated on the same `editReady` signal they already
            // use for remove/edit affordances (mirrors `CteCard`'s body-edit
            // threading). The "focus" drill-in button remains available as an
            // alternative editing surface (e.g. more room, breadcrumb nav).
            <SelectRenderer
              statement={makeNestedSubqueryStatement(
                inlineEdit ? editContext!.statementId : parentStatementId,
                bodyStart,
                inlineEdit ? inlineEdit.end : bodyEnd,
              )}
              bufferText={inlineEdit ? inlineEdit.source : bufferText}
              options={options}
              onEditAsSql={onEditAsSql}
              layout="compact"
              drillTabId={drillTabId}
              editContext={inlineEdit?.ctx}
            />
          ) : (
            <pre
              className="m-0 max-h-72 overflow-auto rounded-md border border-border bg-background p-2 font-mono text-[11px] leading-relaxed"
              data-testid={`${testIdPrefix}-body-text`}
            >
              {bufferText.slice(bodyStart, bodyEnd)}
            </pre>
          )}
        </span>
      ) : null}
    </span>
  );
}

/**
 * COM-24 ("the chip truncates at `i.fi…` — a tooltip with the full subquery
 * SQL would make the truncation harmless"): composes the chip-mode subquery
 * tooltip's content by adding the full inline subquery text (`rawSnippet`) to
 * the existing `chipSubqueryHint` teaching line, so the chip's 40-char
 * `truncateMiddle` label always has an escape hatch to the full SQL.
 *
 * Block mode is untouched — `chipSubqueryHint` already returns `undefined`
 * for `mode === 'block'` (both non-LATERAL, which never reaches this call
 * site — see the `derivedAlias` branch above — and LATERAL, which does), so
 * this stays a pass-through of that existing contract.
 */
function chipTooltipContent(
  mode: SubquerySectionMode,
  isLateral: boolean,
  rawSnippet: string,
): ReactNode {
  const hint = chipSubqueryHint(mode, isLateral);
  if (mode !== 'chip' || !rawSnippet) return hint;
  return (
    <>
      <div>{hint}</div>
      <div className="mt-1 border-t border-primary-foreground/20 pt-1 font-mono">({rawSnippet})</div>
    </>
  );
}

function collapseBody(source: string, start: number, end: number): string {
  return source.slice(start, end).replace(/\s+/g, ' ').trim();
}

function truncated(source: string, start: number, end: number): string {
  const raw = collapseBody(source, start, end);
  if (raw.length <= 40) return raw;
  return `${raw.slice(0, 37)}…`;
}

// Clause-boundary keywords that can legally follow an UNALIASED derived
// table's closing paren (`) WHERE …`, `) ON …`, a further `) JOIN …`, etc.).
// Without this guard a bare-alias scan (no `AS`) would misread the next
// clause's leading keyword as the derived table's alias.
const DERIVED_ALIAS_CLAUSE_KEYWORDS = new Set([
  'where', 'group', 'order', 'having', 'limit', 'offset', 'fetch', 'union',
  'intersect', 'except', 'window', 'for', 'into', 'on', 'using', 'join',
  'inner', 'left', 'right', 'full', 'cross', 'natural', 'lateral', 'and', 'or',
  'set', 'returning', 'values', 'when', 'then', 'else', 'end',
]);

const ALIAS_TOKEN_RE = /^(?:[A-Za-z_][\w$]*|"[^"]*"|\[[^\]]*\]|`[^`]*`)/;

/**
 * Scan the text right after a derived table's closing paren for its alias:
 * `AS alias` (unambiguous — always accepted) or a bare trailing identifier
 * (accepted only when it isn't a clause-boundary keyword, so an unaliased
 * derived table followed directly by `WHERE`/`ON`/`JOIN`/… never misreads
 * that keyword as an alias). Returns null when no alias is present.
 *
 * `bodyEnd` is guaranteed by the `SubqueryRef` extractor to sit right before
 * the matching `)`, separated only by whitespace — so the first non-space
 * char at-or-after `bodyEnd` is always that closing paren for a well-formed
 * derived table.
 */
function deriveDerivedTableAlias(bufferText: string, bodyEnd: number): string | null {
  let i = bodyEnd;
  while (i < bufferText.length && /\s/.test(bufferText[i])) i++;
  if (bufferText[i] !== ')') return null;
  i++;
  while (i < bufferText.length && /\s/.test(bufferText[i])) i++;

  let explicitAs = false;
  const asMatch = /^as\s+/i.exec(bufferText.slice(i));
  if (asMatch) {
    explicitAs = true;
    i += asMatch[0].length;
  }

  const tokenMatch = ALIAS_TOKEN_RE.exec(bufferText.slice(i));
  if (!tokenMatch) return null;
  const token = tokenMatch[0];
  const bare = token.replace(/^["[`]/, '').replace(/["\]`]$/, '');
  if (!explicitAs && DERIVED_ALIAS_CLAUSE_KEYWORDS.has(bare.toLowerCase())) return null;
  return token;
}
